import React, { useEffect, useRef } from 'react';
import { animated, useSpring } from '@react-spring/web';

import { AltBadge } from 'views/Main.js';
import Appearance from 'styles/Appearance.js';
import PageControl from 'views/PageControl.js';
import ProgressBar from 'views/ProgressBar.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';

const Panel = ({ children, className, column, headerStyle, index, options = {}, style, title, utils }) => {

    const { buttons, embedded = false, loading, maxWidth, onRefreshContent, onSizeChange, paging, removeOverflow, removePadding, rightContent, search } = options || {};

    const [animations, setAnimations] = useSpring(() => ({
        top: embedded ? 0 : -50,
        opacity: embedded ? 1 : 0,
        config: { mass: 1, tension: 180, friction: 12 }
    }));
    const contentContainer = useRef(null);

    const onPanelSizeChange = () => {
        onSizeChange({
            height: contentContainer.current.clientHeight,
            width: contentContainer.current.clientWidth
        })
    }

    const onOptionsClick = (targets, evt) => {
        utils.sheet.show({
            items: targets,
            position: 'bottom',
            target: evt.target
        }, key => {
            let target = targets.find(t => t.key === key);
            if(typeof(target?.onClick) === 'function') {
                target.onClick(evt);
            }
        });
    }

    const getAccessories = () => {

        // prepare array for requested buttons and filter out buttons that have been marked as non-visible
        let targets = buttons ? [...buttons] : [];
        targets = targets.filter(target => target.visible !== false);
        
        // fallback to formatting buttons for desktop and tablet
        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                height: '100%',
                padding: '16px 24px 16px 24px'
            }}>
                {targets.map(target => {
                    let color = getButtonColor(target);
                    return (
                        <AltBadge 
                        content={{
                            color: color,
                            text: target.title
                        }} 
                        onClick={target.onClick} 
                        outline={true} />
                    )
                })}
                {rightContent}
                {typeof(onRefreshContent?.onClick) === 'function' && (
                    <img 
                    className={'text-button'}
                    onClick={onRefreshContent.onClick}
                    src={'images/refresh-button-grey-small.png'}
                    style={{
                        height: 30,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 30
                    }} />
                )}
            </div>
        )
    }

    const getButtonColor = target => {
        switch(target.style) {
            case 'danger':
            return Appearance.colors.red;

            case 'dark-grey':
            return Appearance.colors.darkGrey;

            case 'default':
            case 'primary':
            return Appearance.colors.primary();

            case 'grey':
            return Appearance.colors.grey();

            case 'secondary':
            return Appearance.colors.secondary();

            default:
            return target.style;
        }
    }

    const getSearchFields = () => {

        if(!search || !utils) {
            return null;
        }
        
        let { leftContent, filters, onChange, placeholder, rightContent } = search;
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 12,
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%'
                }}>
                    {leftContent}
                    {typeof(onChange) === 'function' && (
                        <TextField
                        containerStyle={{
                            flexGrow: 1
                        }} 
                        icon={'search'}
                        loading={search.loading}
                        onChange={onChange}
                        placeholder={placeholder || 'Search for something...'}
                        useDelay={true}/>
                    )}
                    {filters && (
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            position: 'relative'
                        }}>
                            <div
                            className={'text-button'}
                            onClick={filters.onClick}
                            title={filters.values && Object.keys(filters.values).length > 0 ? 'Edit Filters' : 'Filters'}
                            style={{
                                backgroundColor: filters.values && Object.keys(filters.values).length > 0 ? Appearance.colors.primary() : Appearance.colors.grey(),
                                borderRadius: 15,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                height: 30,
                                marginLeft: 8,
                                minHeight: 30,
                                minWidth: 30,
                                overflow: 'hidden',
                                padding: 7.5,
                                width: 30
                            }}>
                                <img
                                src={'images/filter-icon-white.png'}
                                style={{
                                    height: '100%',
                                    objectFit: 'contain',
                                    width: '100%'
                                }} />
                            </div>
                            {filters.values && Object.keys(filters.values).length > 0  && (
                                <img
                                className={'text-button'}
                                onClick={filters.onReset}
                                src={'images/delete-red-small.png'}
                                title={'Reset Filters'}
                                style={{
                                    height: 30,
                                    marginLeft: 8,
                                    objectFit: 'contain',
                                    width: 30
                                }} />
                            )}
                        </div>
                    )}
                    {rightContent}
                </div>
            </div>
        )
    }

    const runAnimations = async () => {
        try {
            await Utils.sleep(0.2 * index);
            setAnimations({
                top: 0,
                opacity: 1
            })
        } catch(e) {
            console.log(e.message);
        }
    }

    useEffect(() => {
        if(contentContainer.current && typeof(onSizeChange) === 'function') {
            onPanelSizeChange();
            window.addEventListener('resize', onPanelSizeChange);
            return () => {
                window.removeEventListener('resize', onPanelSizeChange);
            }
        }
    }, [contentContainer.current]);

    useEffect(() => {
        runAnimations();
    }, []);

    return (
        <animated.div
        className={`panel window ${className || `${column || 'col-12'} px-4 pt-4 pb-0`}`}
        style={{
            position: 'relative',
            ...animations
        }}>
            <div style={{
                maxWidth: maxWidth,
                paddingTop: title && Utils.isMobile() ? 40 : 0,
                position: 'relative',
                width: '100%',
                ...style
            }}>
                <div style={{
                    height: '100%',
                    ...embedded === true && {
                        ...Appearance.styles.unstyledPanel(),
                        borderColor: Appearance.colors.softBorder(),
                    },
                    ...embedded === false && {
                        ...Appearance.styles.unstyledPanel(),
                        overflow: 'visible'
                    }
                }}>
                    <div style={{
                        alignItems: 'center',
                        borderBottom: `2px solid ${window.theme === 'dark' ? 'rgba(35,35,35,1)' : 'rgba(235,235,235,1)'}`,
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                        ...headerStyle
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minWidth: 0,
                            padding: '16px 24px 16px 24px'
                        }}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                minWidth: 0
                            }}>
                                <img 
                                src={window.theme === 'dark' ? 'images/asterick-badge-dark.png' : 'images/asterick-badge-light.png'}
                                style={{
                                    height: 35,
                                    marginRight: 12,
                                    width: 35
                                }} />
                                <span style={{
                                    ...Appearance.textStyles.panelHeaderText(),
                                    display: 'block',
                                    lineHeight: 1,
                                    maxWidth: '100%',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>{title.toUpperCase()}</span>
                            </div>
                        </div>
                        {getAccessories()}
                    </div>
                    <div
                    className={'card-body p-0'}
                    style={{
                        position: 'relative'
                    }}>
                        {loading && (
                            <div style={{
                                borderRadius: 1,
                                height: 2,
                                left: 0,
                                overflow: 'hidden',
                                position: 'absolute',
                                right: 0,
                                top: 0
                            }}>
                                <ProgressBar/>
                            </div>
                        )}
                        <div
                        ref={contentContainer}
                        className={`card-body-content`}
                        style={{
                            padding: removePadding ? 0 : 24,
                            overflowY: removeOverflow === true ? 'visible' : 'scroll'
                        }}>
                            {getSearchFields()}
                            {children}
                        </div>
                    </div>
                    {paging?.total > 0 && (
                        <div style={{
                            borderTop: `2px solid ${Appearance.colors.divider()}`,
                            width: '100%'
                        }}>
                            <PageControl {...paging} />
                        </div>
                    )}
                </div>
            </div>
        </animated.div>
    )
}
export default Panel;
